import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {
  BASE_URL,
  getToken,
  getCurrentLanguage,
} from "../../shared/utils.js/utils";

const initialState = {
  list: [],
  pagination: null,
  details: null,
  isLoading: false,
  error: null,
  success: null,
};

// get contractors
export const getContractors = createAsyncThunk(
  "contractors/getContractors",
  async (params = {}, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append("page", params.page);
      if (params.per_page) queryParams.append("per_page", params.per_page);
      if (params.search) queryParams.append("search", params.search);

      const url = `${BASE_URL}/contractors${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get contractors!";
      return rejectWithValue(message);
    }
  },
);

// add contractor
export const addContractor = createAsyncThunk(
  "contractors/addContractor",
  async (contractorData, { rejectWithValue }) => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const currentLang = getCurrentLanguage();
      const response = await axios.post(
        `${BASE_URL}/contractors`,
        contractorData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "Accept-Language": currentLang,
          },
        },
      );
      return response.data;
    } catch (error) {
      if (error.response?.data?.errors) {
        const errorMessages = Object.values(error.response.data.errors).flat();
        return rejectWithValue(errorMessages.join(", "));
      }
      const message =
        error.response?.data?.message || "Failed to add contractor!";
      return rejectWithValue(message);
    }
  },
);

// update contractor
export const updateContractor = createAsyncThunk(
  "contractors/updateContractor",
  async ({ id, contractorData }, { rejectWithValue }) => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const currentLang = getCurrentLanguage();
      const response = await axios.put(
        `${BASE_URL}/contractors/${id}`,
        contractorData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "Accept-Language": currentLang,
          },
        },
      );
      return response.data;
    } catch (error) {
      if (error.response?.data?.errors) {
        const errorMessages = Object.values(error.response.data.errors).flat();
        return rejectWithValue(errorMessages.join(", "));
      }
      const message =
        error.response?.data?.message || "Failed to update contractor!";
      return rejectWithValue(message);
    }
  },
);

// delete contractor
export const deleteContractor = createAsyncThunk(
  "contractors/deleteContractor",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.delete(`${BASE_URL}/contractors/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to delete contractor!";
      return rejectWithValue(message);
    }
  },
);

// get contrator details
export const getContractorDetails = createAsyncThunk(
  "contractors/getContractorDetails",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.get(`${BASE_URL}/contractors/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Accept-Language": currentLang,
        },
      });
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to get contractor details!";
      return rejectWithValue(message);
    }
  },
);

// toggle contractor status
export const toggleContractorStatus = createAsyncThunk(
  "contractors/toggleContractorStatus",
  async (id, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();

      if (!token) {
        throw new Error("No authentication token found");
      }

      const response = await axios.patch(
        `${BASE_URL}/contractors/${id}/toggle-status`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Accept-Language": currentLang,
          },
        },
      );
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to toggle contractor status!";
      return rejectWithValue(message);
    }
  },
);

// contractors slice
const contractorsSlice = createSlice({
  name: "contractors",
  initialState,
  reducers: {
    hydrate: (state, action) => {
      // Hydrate state from persisted storage
      if (action.payload) {
        return { ...state, ...action.payload };
      }
      return state;
    },
    clearContractorsError: state => {
      state.error = null;
    },
    clearContractorsSuccess: state => {
      state.success = null;
    },
    clearContractorDetails: state => {
      state.details = null;
    },
    setContractors: (state, action) => {
      state.list = action.payload;
    },
    setContractorsPagination: (state, action) => {
      state.pagination = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(getContractors.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getContractors.fulfilled, (state, action) => {
        state.isLoading = false;
        const data = action.payload?.data;
        state.list = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
            ? data.data
            : Array.isArray(action.payload?.data)
              ? action.payload.data
              : [];
        state.pagination = data?.meta || action.payload?.meta || null;
        state.error = null;
      })
      .addCase(getContractors.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(addContractor.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addContractor.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success =
          action.payload?.message ||
          action.payload?.data?.message ||
          "تم إضافة المقاول بنجاح";
        state.error = null;
      })
      .addCase(addContractor.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updateContractor.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateContractor.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success =
          action.payload?.message ||
          action.payload?.data?.message ||
          "تم تحديث المقاول بنجاح";
        state.error = null;
      })
      .addCase(updateContractor.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(deleteContractor.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteContractor.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success =
          action.payload?.message ||
          action.payload?.data?.message ||
          "تم حذف المقاول بنجاح";
        state.error = null;
        state.list = state.list.filter(
          contractor => contractor.id !== action.meta.arg,
        );
      })
      .addCase(deleteContractor.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(getContractorDetails.pending, state => {
        state.isLoading = true;
        state.error = null;
        state.details = null;
      })
      .addCase(getContractorDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.details = action.payload?.data || action.payload || null;
        state.error = null;
      })
      .addCase(getContractorDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(toggleContractorStatus.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(toggleContractorStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success =
          action.payload?.message ||
          action.payload?.data?.message ||
          "تم تغيير حالة المقاول بنجاح";
        state.error = null;
        const contractor = state.list.find(c => c.id === action.meta.arg);
        if (contractor) {
          contractor.is_active = !contractor.is_active;
        }
        if (state.details && state.details.id === action.meta.arg) {
          state.details.is_active = !state.details.is_active;
        }
      })
      .addCase(toggleContractorStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearContractorsError,
  clearContractorsSuccess,
  clearContractorDetails,
  setContractors,
  setContractorsPagination,
} = contractorsSlice.actions;

export default contractorsSlice.reducer;
